import {
    BRAND_PRIMARY_COLOR_KEY,
    BRAND_SECONDARY_COLOR_KEY,
    MarketplaceStatus,
    saveBrandColorsThunk
} from '@spike/marketplace-action';
import Button from 'components/UI/V3/Button/Button';
import { useSettingsApiClientWrapper } from 'hooks';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import styles from './BrandColors.module.scss';
import BrandColorsPreview from './BrandColorsPreview';
import HexColorField, { HEX_COLOR_REGEX } from './HexColorField';

export const DEFAULT_BRAND_PRIMARY_COLOR = '#EAB464';
export const DEFAULT_BRAND_SECONDARY_COLOR = '#92B4A7';

interface BrandColorsProps {
    disabled?: boolean;
}

const BrandColors: FunctionComponent<BrandColorsProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useSettingsApiClientWrapper();

    const settings = useSelector((state: RootState) => state.marketplace.settingsMarketplace.settings);
    const status = useSelector((state: RootState) => state.marketplace.status);

    const savedPrimary =
        settings.find(setting => setting.key === BRAND_PRIMARY_COLOR_KEY)?.value || DEFAULT_BRAND_PRIMARY_COLOR;
    const savedSecondary =
        settings.find(setting => setting.key === BRAND_SECONDARY_COLOR_KEY)?.value || DEFAULT_BRAND_SECONDARY_COLOR;

    const [primary, setPrimary] = useState(savedPrimary);
    const [secondary, setSecondary] = useState(savedSecondary);

    useEffect(() => {
        setPrimary(savedPrimary);
        setSecondary(savedSecondary);
    }, [savedPrimary, savedSecondary]);

    const isPrimaryValid = HEX_COLOR_REGEX.test(primary);
    const isSecondaryValid = HEX_COLOR_REGEX.test(secondary);
    const isSaving = status === MarketplaceStatus.SavingBrandColors;
    const hasChanges = primary !== savedPrimary || secondary !== savedSecondary;

    const saveHandler = () => {
        dispatch(saveBrandColorsThunk(apiClientWrapper, primary, secondary));
    };

    return (
        <div className={styles.container}>
            <p className={styles.intro}>
                Pick your brand colors. This color will be used for buttons and progress elements. You can change it
                anytime.
            </p>
            <div className={styles.content}>
                <div className={styles.fields}>
                    <HexColorField
                        title="Primary Color"
                        description="Pick the main color that represents your brand."
                        value={primary}
                        fallbackColor={DEFAULT_BRAND_PRIMARY_COLOR}
                        disabled={props.disabled}
                        onChange={setPrimary}
                        onReset={() => setPrimary(DEFAULT_BRAND_PRIMARY_COLOR)}
                    />
                    <HexColorField
                        title="Secondary Color"
                        description="Choose your secondary brand color."
                        value={secondary}
                        fallbackColor={DEFAULT_BRAND_SECONDARY_COLOR}
                        disabled={props.disabled}
                        onChange={setSecondary}
                        onReset={() => setSecondary(DEFAULT_BRAND_SECONDARY_COLOR)}
                    />
                    <Button
                        label={isSaving ? 'Saving...' : 'Save'}
                        size="large"
                        isLoading={isSaving}
                        disabled={props.disabled || isSaving || !isPrimaryValid || !isSecondaryValid || !hasChanges}
                        onClick={saveHandler}
                        className={styles.saveButton}
                    />
                </div>
                <BrandColorsPreview
                    primaryColor={isPrimaryValid ? primary : savedPrimary}
                    secondaryColor={isSecondaryValid ? secondary : savedSecondary}
                />
            </div>
        </div>
    );
};

export default BrandColors;
